import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, ScanLine, ArrowLeft, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Fullscreen QR scanner used by Kiosko volunteers.
 * When a QR is detected, extracts the URL/id and navigates to /checkin/{id}?token=…
 * so the existing check-in flow takes over (fetch + show status buttons).
 */
export default function CheckInScanner() {
  const nav = useNavigate();
  const scannerRef = useRef(null);
  const containerId = "kiosko-qr-reader";
  const [status, setStatus] = useState("starting"); // starting | scanning | error | stopped
  const [errorMsg, setErrorMsg] = useState("");
  const [lastScan, setLastScan] = useState("");
  const [zoom, setZoom] = useState(1);
  const [zoomCap, setZoomCap] = useState(null); // {min, max, step} or null if unsupported

  // Adjust the video track's zoom if the camera supports it.
  const applyZoom = async (value) => {
    setZoom(value);
    try {
      const scanner = scannerRef.current;
      if (!scanner) return;
      const videoEl = document.querySelector(`#${containerId} video`);
      const stream = videoEl?.srcObject;
      const track = stream?.getVideoTracks?.()[0];
      if (!track) return;
      const caps = track.getCapabilities?.();
      if (caps?.zoom) {
        await track.applyConstraints({ advanced: [{ zoom: value }] });
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      nav("/kiosko");
      return;
    }
    let stopped = false;
    const html5Q = new Html5Qrcode(containerId, /* verbose */ false);
    scannerRef.current = html5Q;

    const parseAndNavigate = (raw) => {
      // Prevent duplicate consecutive scans firing multiple times
      if (stopped) return;
      setLastScan(raw);
      // Accept either full URL "/checkin/<id>?token=xxx" or bare id
      try {
        let target = null;
        if (raw.startsWith("http")) {
          const u = new URL(raw);
          if (u.pathname.startsWith("/checkin/")) {
            target = u.pathname + u.search;
          }
        } else if (raw.startsWith("/checkin/")) {
          target = raw;
        } else if (/^[a-f0-9-]{20,}$/i.test(raw)) {
          target = `/checkin/${raw}`;
        }
        if (!target) {
          toast.error("QR no reconocido: " + raw.slice(0, 40));
          return;
        }
        stopped = true;
        try { html5Q.stop().catch(() => {}); } catch { /* noop */ }
        toast.success("QR detectado, cargando…");
        // HARD navigation so the /checkin/:id page mounts fresh every time
        // (React Router client-side nav sometimes leaves the fetch hanging
        // when coming from a heavy camera stream).
        window.location.assign(target);
      } catch (e) {
        toast.error("QR inválido");
      }
    };

    (async () => {
      try {
        // Use facingMode:"environment" instead of a specific device id — this
        // lets the phone pick its DEFAULT (main 1x) rear camera instead of
        // grabbing the ultra-wide 0.5x lens (which distorts the QR).
        await html5Q.start(
          { facingMode: { ideal: "environment" } },
          {
            fps: 15,
            qrbox: { width: 260, height: 260 },
            // Request a sane resolution — helps the browser choose a
            // proper camera track rather than the widest-angle one.
            videoConstraints: {
              facingMode: { ideal: "environment" },
              width: { ideal: 1280 },
              height: { ideal: 720 },
              advanced: [{ zoom: 2 }],  // ignored if unsupported; nudges 1x/2x on multi-lens phones
            },
          },
          (decoded) => parseAndNavigate(decoded),
          () => {} // ignore per-frame decode misses
        );
        setStatus("scanning");
        // After camera starts, inspect the video track's capabilities so we can
        // expose a zoom slider only if the hardware supports optical zoom.
        setTimeout(() => {
          const videoEl = document.querySelector(`#${containerId} video`);
          const track = videoEl?.srcObject?.getVideoTracks?.()[0];
          const caps = track?.getCapabilities?.();
          if (caps?.zoom) {
            setZoomCap({ min: caps.zoom.min || 1, max: caps.zoom.max || 3, step: caps.zoom.step || 0.1 });
            // start at 1x for a normal field of view (no distortion)
            track.applyConstraints({ advanced: [{ zoom: 1 }] }).catch(() => {});
            setZoom(1);
          }
        }, 400);
      } catch (e) {
        console.error(e);
        setErrorMsg(e?.message || "No se pudo iniciar la cámara. Revisa permisos.");
        setStatus("error");
      }
    })();

    return () => {
      stopped = true;
      try { html5Q.stop().catch(() => {}); } catch { /* noop */ }
      try { html5Q.clear(); } catch { /* noop */ }
    };
  }, [nav]);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => nav("/admin/logistica")}
            data-testid="scan-back-btn"
            className="text-white/70 hover:text-yellow-400 flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Volver
          </button>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-400/70">Kiosco</div>
            <h1 className="font-heading uppercase text-lg tracking-wide">Escanear QR</h1>
          </div>
          <button
            onClick={() => nav("/admin/logistica")}
            className="text-white/50 hover:text-red-400"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Camera area */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-6">
        <div className="w-full max-w-md">
          <div className="relative rounded-2xl overflow-hidden border-2 border-yellow-400/40 bg-black" style={{ aspectRatio: "1 / 1" }}>
            <div id={containerId} data-testid="qr-reader" className="absolute inset-0" />
            {/* Corner brackets overlay */}
            <div className="pointer-events-none absolute inset-6 border-2 border-yellow-400/90 rounded-xl" style={{
              WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 25%, transparent 25%, transparent 75%, black 75%, black 100%), linear-gradient(to right, black 0%, black 25%, transparent 25%, transparent 75%, black 75%, black 100%)",
              WebkitMaskComposite: "source-over",
              maskComposite: "add",
            }} />
            {status === "starting" && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-yellow-400">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <div className="text-sm">Iniciando cámara…</div>
                </div>
              </div>
            )}
            {status === "error" && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/85 text-white px-6 text-center">
                <div>
                  <Camera className="w-10 h-10 mx-auto mb-2 text-red-400" />
                  <div className="text-red-300 font-bold mb-1">No pude abrir la cámara</div>
                  <div className="text-xs text-white/60 mb-4">{errorMsg}</div>
                  <div className="text-xs text-white/50">
                    Permite el acceso a la cámara desde la configuración del navegador y recarga.
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <div className="inline-flex items-center gap-2 text-yellow-400/80 text-sm">
              <ScanLine className="w-4 h-4" />
              Apunta al código QR del brazalete
            </div>
            {lastScan && (
              <div className="mt-2 text-[10px] text-white/40 font-mono truncate">
                Último: {lastScan.slice(0, 60)}
              </div>
            )}
          </div>

          {/* Zoom controls — only shown if the phone camera supports optical zoom */}
          {zoomCap && (
            <div className="mt-4 bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-white/60 mb-2">
                <span>Zoom</span>
                <span className="font-mono text-yellow-300">{zoom.toFixed(1)}x</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => applyZoom(Math.max(zoomCap.min, +(zoom - (zoomCap.step * 5)).toFixed(1)))}
                  data-testid="zoom-out-btn"
                  className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white text-lg font-bold flex items-center justify-center"
                >−</button>
                <input
                  type="range"
                  min={zoomCap.min}
                  max={zoomCap.max}
                  step={zoomCap.step}
                  value={zoom}
                  onChange={(e) => applyZoom(parseFloat(e.target.value))}
                  data-testid="zoom-slider"
                  className="flex-1 accent-yellow-400"
                />
                <button
                  onClick={() => applyZoom(Math.min(zoomCap.max, +(zoom + (zoomCap.step * 5)).toFixed(1)))}
                  data-testid="zoom-in-btn"
                  className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white text-lg font-bold flex items-center justify-center"
                >+</button>
              </div>
              <p className="text-[10px] text-white/40 mt-1 text-center">
                Sube el zoom si el QR se ve muy pequeño o borroso
              </p>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-2">
            <button
              onClick={() => nav("/admin/logistica")}
              data-testid="scan-open-list-btn"
              className="text-xs text-white/50 hover:text-yellow-400 underline"
            >
              O buscar por nombre en la lista
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
